<template>
  <!-- 人物特征 -->
  <div>
    <div class="flex flex-col gap-2 px-4">
      <div class="flex justify-between items-center mt-4">
        <div class="font-bold">
          {{ t(`painting.people.feature`) }}
        </div>
        <div>
          <button
            class="btn btn-xs btn-primary rounded"
            @click="showCreateTraitModal = true"
          >
            {{ t(`painting.people.new`) }}
          </button>
        </div>
      </div>
      <div class="flex relative" v-if="selectedTrait">
        <InputTag
          v-model:prompt="matePrompt"
          :show-negative="false"
          :show-upgrade-prompt="false"
        />
        <!-- <VanField
          v-model="matePrompt"
          :autosize="{ maxHeight: 90, minHeight: 20 }"
          :disabled="!enableEditTrait"
          rows="1"
          class="pr-10"
          type="textarea"
        /> -->
        <button
          v-if="enableEditTrait"
          class="absolute right-1 bottom-4 btn btn-xs"
          @click="showCharacterEditModal = true"
        >
          <i class="iconify mingcute--edit-3-line"></i>
        </button>
      </div>
      <mate-trait-selection
        enable-selection
        enable-edit
        :selected-trait="selectedTrait"
        :enableSelectionLock="enableSelectionLock"
        @update:selected-trait="clickSelectedTrait"
        @update:showCreateTraitModal="showCreateTraitModal = $event"
      />

      <!-- 人物 -->
      <EditPromptsContainer
        mode="character"
        v-model:prompt="matePrompt"
        :has-preview="false"
        v-model:show-modal="showCharacterEditModal"
        :has-negative="false"
      />
    </div>
    <!-- 表情 -->
    <EditPromptsContainer
      mode="faceExpression"
      v-model:prompt="reqData.faceExpression.prompt"
      v-model:negative-prompt="reqData.faceExpression.negativePrompt"
    >
      <template #left>
        <i class="iconfont icon-emoji mr-1"></i>
        <span class="font-bold">
          {{ t(`painting.people.faceExpression`) }}
        </span>
      </template>
    </EditPromptsContainer>

    <!-- 姿势 -->
    <EditPromptsContainer
      mode="pose"
      v-model:prompt="reqData.pose.prompt"
      v-model:negative-prompt="reqData.pose.negativePrompt"
      :is-unlimited="reqData.isUnlimited"
    >
      <template #left>
        <i class="iconfont icon-pray mr-1"></i>
        <span class="font-bold">
          {{ t(`painting.people.pose`) }}
        </span>
      </template>
      <template #right>
        <div class="flex gap-1 justify-end flex-wrap">
          <template v-if="!reqData.pose.media && !reqData.pose.customPoseUrl">
            <button class="btn btn-xs rounded" @click="clickPoseModal(0)">
              {{ t(`painting.people.posePreset`) }}
            </button>
            <button class="btn btn-xs rounded" @click="clickPoseModal(3)">
              {{ t(`painting.people.poseDiy`) }}
            </button>
            <button
              class="btn btn-xs rounded"
              @click="showImgUploadPopup = true"
            >
              {{ t(`painting.people.uploadPose`) }}
            </button>
          </template>
          <button
            v-else
            class="btn btn-xs rounded"
            @click="
              (reqData.pose.media = undefined),
                (reqData.pose.mediaId = undefined),
                (reqData.pose.customPoseUrl = undefined)
            "
          >
            {{ $t("Buttons.delete") }}
          </button>
        </div>
      </template>

      <template #center>
        <div class="flex flex-col gap-2 px-4 mb-4">
          <div class="flex justify-center items-center">
            <div
              class="h-[12rem]"
              v-if="reqData.pose.media || reqData.pose.customPoseUrl"
            >
              <VanImage
                class="w-full h-full flex-1"
                :src="
                  reqData.pose.media?.url ||
                  (reqData.pose.customPoseUrl && reqData.pose.customPoseUrl[0])
                "
                fit="cover"
                @click="showImgUploadPopup = true"
              />
            </div>
          </div>
        </div>
      </template>
    </EditPromptsContainer>
    <!-- 服饰 -->
    <EditPromptsContainer
      mode="clothing"
      v-model:prompt="reqData.clothing.prompt"
      v-model:negative-prompt="reqData.clothing.negativePrompt"
      :is-unlimited="reqData.isUnlimited"
    >
      <template #left>
        <i class="iconfont icon-tshirt mr-1"></i>
        <span class="font-bold">
          {{ t(`painting.people.clothing`) }}
        </span>
      </template>
    </EditPromptsContainer>
    <EditPromptsContainer
      v-if="reqData.isUnlimited"
      mode="other"
      v-model:prompt="reqData.clothing.prompt"
      v-model:negative-prompt="reqData.clothing.negativePrompt"
      :is-unlimited="reqData.isUnlimited"
    >
      <template #left>
        <i class="iconfont icon-heart-fill mr-1"></i>
        <span class="font-bold"> NSFW </span>
      </template>
    </EditPromptsContainer>
  </div>

  <CreateTraitPresetModal v-model:show="showCreateTraitModal" />

  <ImageTabsTraitPose
    type="Pose"
    v-model:showPoseEdit="showPoseEdit"
    v-model:showPoseType="showPoseType"
    @update:selectedMedia="onSelectPose"
    @update:selectedCustomPose="onSelectCustomPose"
  />

  <media-select-popup
    @on-media-selected="onMediaSelected"
    enable-use-avatar
    media-type="img"
    :multiple="false"
    v-model:show="showImgUploadPopup"
    :upload-type="7"
    :max-count="1"
    :enable-crop="true"
    :enable-go-image-gen="false"
    :cropAspectRatio="cropAspectRatio"
    showRatioSelect
    @update:crop-aspect-ratio="onCropAspectRatioChange"
    replace
  />
</template>

<script lang="ts" setup>
import { useMateTrait } from "@/stores/mateTrait";
import { StorageGet, StorageSet } from "@/tools/localStorage";
import { MediaUploadFileItem } from "@/types/component";
import { showConfirmDialog } from "vant";
import { ref, PropType, watch, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { initStyle } from "./initStyle";

const props = defineProps({
  reqData: {
    type: Object as PropType<PromptJson>,
    required: true,
  },
  enableSelectionLock: {
    type: Boolean,
    default: false,
  },
  enableEditTrait: {
    type: Boolean,
    default: true,
  },
});

const trait = useMateTrait();
const { t } = useI18n();

const showCreateTraitModal = ref(false);

// 人物特征
const selectedTrait = ref<MateTrait | null>();
const showCharacterEditModal = ref(false);
// 选择特征
const matePrompt = ref("");
const clickSelectedTrait = (trait: MateTrait | null) => {
  selectedTrait.value = trait;
  matePrompt.value = trait?.prompt ?? "";
  if (trait) {
    const { style, mixStyle, customStyle } = trait;
    initStyle(props.reqData, style, customStyle, mixStyle);
  }
};

watch(matePrompt, (val) => (props.reqData.matePrompt = val || ""));

watch(
  selectedTrait,
  (val) => (props.reqData.mateTraitId = val?.id || undefined)
);

watch(
  () => props.reqData.mateTraitId,
  async (val) => {
    initMateTrait(val || undefined);
  },
  {
    deep: true,
  }
);

// 姿势
const showPoseEdit = ref(false);
const showPoseType = ref(0);

const showImgUploadPopup = ref(false);
const clickPoseModal = async (tab: number) => {
  if (tab === 3) {
    try {
      if (!StorageGet(`Message.customPoseTip`)) {
        await showConfirmDialog({
          message: t(`Message.customPoseTip`),
          confirmButtonText: t(`Buttons.confirm`),
          cancelButtonText: t(`Buttons.cancel`),
        });
        StorageSet(`Message.customPoseTip`, 1);
      }
    } catch (e) {
      return;
    }
  }

  showPoseType.value = tab;
  showPoseEdit.value = true;
};

const onSelectPose = (list: MediaUploadFileItem) => {
  // poseImg.value = list;
  // @ts-ignore
  props.reqData.pose.media = { ...list };
  props.reqData.pose.mediaId = list.mediaId;
  props.reqData.pose.customPoseUrl = undefined;
  props.reqData.ratio = list.ratio;
  showPoseEdit.value = false;
};

const onSelectCustomPose = (list: {
  ratio: string;
  customPoseUrl: string[];
}) => {
  props.reqData.pose.customPoseUrl = list.customPoseUrl;
  props.reqData.pose.media = undefined;
  props.reqData.pose.mediaId = undefined;
  props.reqData.ratio = list.ratio;
  showPoseEdit.value = false;
};

// 裁剪上传
const cropAspectRatio = ref(3 / 4);
const onMediaSelected = (list: MediaUploadFileItem[]) => {
  if (list.length > 0) {
    // @ts-ignore
    props.reqData.pose.media = { url: list[0].url };
    props.reqData.pose.mediaId = list[0].mediaId;
  }
};
const onCropAspectRatioChange = (_ratio: string) => {
  props.reqData.ratio = _ratio;
  const [w, h] = _ratio.split(":").map(Number);
  cropAspectRatio.value = w / h;
};

const initMateTrait = async (id?: number, prompt?: string) => {
  if (!id) return;
  if (id && selectedTrait.value?.id !== id) {
    selectedTrait.value = await trait.getOrFetchTraitItem(id);
    matePrompt.value = prompt ?? selectedTrait.value?.prompt ?? "";
  }
};

onMounted(async () => {
  initMateTrait(
    props.reqData.mateTraitId || undefined,
    props.reqData.matePrompt
  );
});
</script>
